import React, { useState, useEffect } from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { schoolService } from '../../../services/schoolService';
import { adminService } from '../../../services/adminService';

const FilterPanel = ({ filters, onFilterChange, onReset, onExport }) => {
  const [schools, setSchools] = useState([]);
  const [schoolSearchTerm, setSchoolSearchTerm] = useState('');
  const [cities, setCities] = useState([]);
  const cityOptions = [
    { value: 'all', label: 'All Cities' },
    { value: 'mumbai', label: 'Mumbai' },
    { value: 'nagpur', label: 'Nagpur' }
  ];

  const ageGroupOptions = [
    { value: 'all', label: 'All Age Groups' },
    { value: 'boysU14', label: 'Boys U14' },
    { value: 'girlsU15', label: 'Girls U15' },
    { value: 'boysU16', label: 'Boys U16' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'pending', label: 'Under Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' }
  ];

  const genderOptions = [
    { value: 'all', label: 'All Genders' },
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' }
  ];

  useEffect(() => {
    loadCities();
  }, []);

  useEffect(() => {
    // Only search if user has typed at least 2 characters
    if (schoolSearchTerm && schoolSearchTerm.trim().length >= 2) {
      const timer = setTimeout(() => {
        loadSchools(schoolSearchTerm);
      }, 300); // 300ms debounce

      return () => clearTimeout(timer);
    } else {
      // Clear schools if search term is too short or empty
      setSchools([]);
    }
  }, [schoolSearchTerm, filters?.city]);

  const loadCities = async () => {
    try {
      const data = await adminService.getCitySettings();
      setCities(data || []);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const loadSchools = async (searchTerm = '') => {
    try {
      if (!searchTerm || searchTerm.trim().length < 2) {
        setSchools([]);
        return;
      }

      const schoolData = await schoolService.searchSchools(
        searchTerm,
        filters?.city !== 'all' ? filters?.city : null,
        20 // Limit to 20 results
      );
      
      setSchools(schoolData || []);
    } catch (error) {
      console.error('Error loading schools:', error);
      setSchools([]);
    }
  };

  const handleSchoolToggle = (schoolName) => {
    const currentSelected = filters?.schoolNames || [];
    let newSelected;
    if (currentSelected.includes(schoolName)) {
      // Remove from selection
      newSelected = currentSelected.filter(name => name !== schoolName);
    } else {
      // Add to selection
      newSelected = [...currentSelected, schoolName];
    }
    onFilterChange('schoolNames', newSelected);
  };

  const handleCityChange = (value) => {
    onFilterChange('city', value);
    // Clear school selections when city changes
    onFilterChange('schoolNames', []);
    setSchoolSearchTerm('');
  };

  return (
    <div className="card">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-foreground">Filters</h3>
        <Button variant="ghost" size="sm" onClick={onReset} iconName="RotateCcw" className="touch-target">
          Reset
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-3 sm:mb-4">
        <Select
          label="City"
          options={cityOptions}
          value={filters?.city}
          onChange={handleCityChange}
        />

        <Select
          label="Age Group"
          options={ageGroupOptions}
          value={filters?.ageGroup}
          onChange={(value) => onFilterChange('ageGroup', value)}
        />

        <Select
          label="Gender"
          options={genderOptions}
          value={filters?.gender}
          onChange={(value) => onFilterChange('gender', value)}
        />

        <Select
          label="Status"
          options={statusOptions}
          value={filters?.status}
          onChange={(value) => onFilterChange('status', value)}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
        {/* School Multi-Select */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
                School {filters?.schoolNames?.length > 0 && (
                  <span className="text-blue-600">({filters.schoolNames.length} selected)</span>
                )}
              </label>
              
              {/* Selected Schools Display */}
              {filters?.schoolNames && filters?.schoolNames.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                  {filters.schoolNames.map((schoolName, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium"
                    >
                      {schoolName}
                      <button
                        type="button"
                        onClick={() => {
                          const newSelected = filters.schoolNames.filter(name => name !== schoolName);
                          onFilterChange('schoolNames', newSelected);
                        }}
                        className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                        aria-label={`Remove ${schoolName}`}
                      >
                        <span className="text-blue-700 hover:text-red-600 font-bold">×</span>
                      </button>
                    </span>
                  ))}
                </div>
              )}
              
              <div className="relative">
                <input
                  type="text"
                  value={schoolSearchTerm}
                  onChange={(e) => {
                    setSchoolSearchTerm(e.target.value);
                  }}
                  placeholder="Type at least 2 characters to search..."
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    filters?.schoolNames?.length > 0
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-300'
                  }`}
                />
                {schoolSearchTerm && schoolSearchTerm.trim().length < 2 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Type at least 2 characters to search schools
                  </p>
                )}
                {schoolSearchTerm && schoolSearchTerm.trim().length >= 2 && schools.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    No schools found matching "{schoolSearchTerm}"
                  </p>
                )}
                {schoolSearchTerm && schoolSearchTerm.trim().length >= 2 && schools.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto touch-target">
                    <button
                      type="button"
                      onClick={() => {
                        onFilterChange('schoolNames', []);
                        setSchoolSearchTerm('');
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm font-medium border-b"
                    >
                      Clear All Selections
                    </button>
                    {schools.map(school => {
                      const isSelected = filters?.schoolNames?.includes(school.name) || false;
                      return (
                        <button
                          key={school.id}
                          type="button"
                          onClick={() => handleSchoolToggle(school.name)}
                          className={`w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center gap-2 ${
                            isSelected ? 'bg-blue-100 border-l-2 border-blue-500' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}} // Handled by button onClick
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className={isSelected ? 'font-medium text-blue-700 flex-1' : 'flex-1'}>
                            {school.name}
                            {school.city && (
                              <span className="text-xs text-gray-500 ml-2">({school.city})</span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                    {schools.length >= 20 && (
                      <div className="px-4 py-2 text-xs text-gray-500 border-t">
                        Showing first 20 results. Refine your search for more specific results.
                      </div>
                    )}
                  </div>
                )}
              </div>
        </div>

      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="default" onClick={onExport} iconName="Download" iconPosition="left">
          Export Data
        </Button>
      </div>
    </div>
  );
};

export default FilterPanel;